import { article, type SegmentNoun } from "@keydials/dial-workspace";

// ---------------------------------------------------------------------------
// Every word the builder shows (KEY-560), as approved in the builder copy sheet on 14 Sep 2026. In one place so the decisions
// taken there hold everywhere, and `copy.test.ts` checks them:
//   · "test copy" in this app, never "test dial"
//   · "Go live", never "put it live"
//   · "design" only where something counts against the plan (plan meter, limit sheets) and in the Design settings section
// Placeholders in braces are filled by `fill`; the dial's own word for an action point comes from `nounValues`.
// ---------------------------------------------------------------------------

export const builderCopy = {
  waysIn: {
    buildButton: "Build a dial",
    emptyHeading: "Build your first Key Dial",
    emptyLine: "Describe your process and we'll draft it, start from a template, or start with an empty wheel.",
    describeIt: "Describe it",
    useTemplate: "Use a template",
    startBlank: "Start blank",
    shelfHeading: "Being built",
    carryOn: "Carry on building",
    discard: "Discard",
    discardConfirm: "Discard “{name}”? It's deleted, and stops counting towards your plan.",
    discardFailed: "It couldn't be discarded. Try again in a moment.",
    states: { Building: "Building", TestCopy: "Test copy", Running: "Running" },
  },

  frame: {
    stages: { start: "Start", build: "Build", colours: "Colours", details: "Details", launch: "Launch" },
    stagesLabel: "Stages",
    saveAndFinishLater: "Save and finish later",
    saved: "Saved just now",
    saving: "Saving…",
    notSaved: "Not saved. Check your connection and try again.",
    untitled: "Your dial",
    feedback: "Feedback",
    loading: "Loading…",
    continue: "Continue",
    tryAgain: "Try again",
    couldNotOpen: "This dial couldn't be opened. Try again in a moment.",
  },

  start: {
    question: "What is this dial called, and what is it for?",
    nameLabel: "Name",
    namePlaceholder: "Site Safety Check",
    purposeLabel: "What it's for",
    purposePlaceholder: "Our monthly check of each depot",
    bothChangeable: "You can change both at any time.",
    startingQuestion: "How do you want to start?",
    recommended: "Recommended",
    describe: {
      title: "Describe it",
      body: "Tell us about your process in a paragraph, and add a document if you have one. We'll draft the dial for you to change.",
      bodyNoDocument: "Tell us about your process in a paragraph. We'll draft the dial for you to change.",
      left: "{n} AI drafts left this month",
      locked: "10 AI drafts a month on Silver and above",
      resets: "Resets on {date}",
      fieldLabel: "Describe your process",
      fieldPlaceholder: "What gets checked, how often, and by whom. A paragraph is plenty.",
      draft: "Draft my dial",
      drafting: "Drafting your dial. This can take a minute.",
      draftingFromDocument: "Drafting your dial from your document. This can take a couple of minutes.",
      stop: "Stop",
      failed: "The draft couldn't be made. Try again, or start from a template.",
      tooLong: "That's too much to draft in one go. Try a shorter document, or remove it.",
      aiUnavailable: "AI drafts aren't available right now. Start from a template, or start blank.",
      document: {
        add: "Add a document",
        hint: "A PDF, Word, Excel or text file, up to 10 MB. We'll take the sections from it and follow what you wrote above.",
        reading: "Reading {name}…",
        trimmed: "Only the first part fits, so we'll draft from that.",
        replace: "Replace",
        remove: "Remove",
        wrongType: "That kind of file can't be read. Try a PDF, Word, Excel or text file.",
        tooLarge: "That file is over 10 MB. Try a smaller one, or paste the important part above.",
        unreadable: "{name} couldn't be read. If it's a scan, try a copy with text in it.",
        unavailable: "Documents can't be added right now. Describe your process instead.",
        failed: "That document couldn't be read just now. Try again in a moment.",
      },
    },
    template: {
      title: "Use a template",
      body: "Start from a dial built for your sector, then change whatever you like.",
      sectorLabel: "Sector",
      allSectors: "All sectors",
      none: "No templates for this sector yet.",
      unavailable: "Templates couldn't be loaded. You can still start blank.",
    },
    blank: { title: "Start blank", body: "An empty wheel. Add your own sections and {nouns}." },
    forkQuestion: "What does each {noun} hold?",
    oneColour: {
      title: "One colour per {noun}",
      body: "Someone taps a slice and picks its colour. That is the whole job.",
      goodFor: "Good for compliance checks, standards, anything with one answer per area.",
    },
    checklist: {
      title: "A checklist inside each {noun}",
      body: "Each item gets its own colour. The slice follows the list: red with none done, amber with some, green with all.",
    },
    forkNote: "You can switch while you're building. Once the dial is running, this stays as it is.",
    templateChose: "This template has a checklist inside each {noun}.",
    nounQuestion: "What do you call the things on your dial?",
    nounHint: "For example: checks, standards, tasks. Leave it empty to say “action points”.",
    nounOne: "One",
    nounMany: "More than one",
    creating: "Creating your dial…",
    switchToOneColour: "Switch to one colour per {noun}? The items in every checklist are removed.",
    switchToOneColourWithItemFields:
      "Switch to one colour per {noun}? The items in every checklist are removed, and so are the fields asked of each item: {name}.",
    switchToOneColourItemFieldsOnly: "Switch to one colour per {noun}? The fields asked of each item are removed: {name}.",
    switchConfirm: "Switch",
    keepChecklists: "Keep the checklists",
  },

  build: {
    prompt: "Tap a section to change it. Add the areas your process has.",
    addFirstSection: "Add your first section",
    ghostAddSegment: "Add {aNoun}",
    readinessOne: "1 thing before you can go live",
    readinessMany: "{n} things before you can go live",
    ready: "Ready to go live",
    sectionEyebrow: "Section · {n} of {count}",
    segmentEyebrow: "{Noun} · {n} of {count}",
    untitled: "No name yet",
    nameLabel: "Name",
    aboutLabel: "What's this section about?",
    optional: "optional",
    aboutPlaceholder: "One sentence for the team",
    colourLabel: "Colour on the dial",
    listHeading: "{Nouns} in this section · {n}",
    addSegment: "+ Add {aNoun}",
    addSection: "+ Add a section",
    rename: "Rename",
    moveUp: "Move up",
    moveDown: "Move down",
    remove: "Remove",
    removeConfirm: "Remove “{name}”?",
    keepIt: "Keep it",
    suggest: "Suggest with AI",
    suggestLeft: "{n} left this month",
    suggestLocked: "Included with Silver and above",
    moreOptions: "More options",
    notesForTeam: "Notes for the team",
    helpSectionTitle: "What's a section?",
    helpSectionBody: "The big areas of your process. Most dials have three to six. You can rename it any time.",
    helpSegmentTitle: "What's {aNoun}?",
    helpSegmentBody: "One thing someone keeps on top of inside a section. It's what people mark as they go.",
    itemsHeading: "Items in this {noun} · {n}",
    addItem: "+ Add an item",
    suggestChecklist: "Suggest a checklist",
    reviewDate: "Can be given a review date",
    itemCountOne: "1 item",
    itemCountMany: "{n} items",
    itemCountNone: "No items yet",
    escapeHatch: "Need something the builder can't do? Tell us",
    problems: {
      noSections: "Your dial has no sections yet.",
      noSegments: "{name} has no {nouns} yet.",
      noSegmentsUnnamed: "A section with no name has no {nouns} yet.",
      noItems: "{name} has no items yet.",
      noItemsUnnamed: "One of your {nouns} has no name and no items yet.",
    },
  },

  colours: {
    question: "How will people mark progress?",
    presets: { "traffic-light": "Traffic light", "to-do-and-done": "To do and done", workflow: "Workflow" },
    presetConfirm: "Switch to {name}? The states your dial has now are replaced.",
    presetSwitch: "Switch",
    keepStates: "Keep these states",
    customise: "Customise",
    colourLabel: "Colour for {name}",
    countsAsDone: "Counts as done",
    countsAsDoneHint: "States that count as done fill the ring in the middle of the dial.",
    addState: "+ Add a state",
    rename: "Rename",
    remove: "Remove",
    checklistRollup: "Each {noun} follows its list: red with none done, amber with some, green with all.",
    reviewDates: "Any state can carry a review date, so people know when to look again.",
  },

  details: {
    question: "What should people record, and where?",
    skip: "Skip for now",
    skipHint: "You can add details later, from the dial's Design settings.",
    openingLine:
      "Every {noun} and item can already hold files. Add a File field only for a document that belongs in a named place, like a certificate.",
    openingLineOneColour:
      "Every {noun} can already hold files. Add a File field only for a document that belongs in a named place, like a certificate.",
    places: { dial: "The whole dial", section: "Each section", segment: "Each {noun}", item: "Each item" },
    placeHints: {
      dial: "Asked once, for the whole dial. For example, the site's address.",
      section: "Asked of every section. For example, who leads it.",
      segment: "Asked of every {noun}. For example, when it was last checked.",
      item: "Asked of every item. For example, a serial number.",
    },
    fieldCountNone: "No fields yet",
    fieldCountOne: "1 field",
    fieldCountMany: "{count} fields",
    addField: "+ Add a field",
    typePickerHeading: "What kind of answer?",
    cancel: "Cancel",
    tableType: "Table",
    personType: "Person from the dial’s Who’s who",
    contactType: "Contact from the dial’s contacts list",
    responsibleType: "Responsible person",
    unnamedField: "A field with no name",
    types: {
      shortText: { label: "Short text", hint: "A name, a reference, a few words" },
      longText: { label: "Long text", hint: "Notes that run to a paragraph" },
      date: { label: "Date", hint: "An inspection date, an expiry" },
      choice: { label: "Choice", hint: "Pick from a list you write" },
      person: { label: "Person", hint: "Who did it, or who's responsible" },
      email: { label: "Email", hint: "An email address" },
      phone: { label: "Phone", hint: "A phone number" },
      file: { label: "File", hint: "A document in a named place" },
      photo: { label: "Photo", hint: "A picture, taken or uploaded" },
      video: { label: "Video", hint: "A short clip" },
    },
    options: "Options",
    peopleAddOwn: "People can add their own",
    group: "Group",
    startsFolded: "Starts folded",
    suggestedAnswer: "Suggested answer",
    suggestedAnswerHint: "People can use it or write their own.",
    requiredAtFirstRun: "Must be answered before the dial opens",
    requiredForNode: "Must be filled before this {noun} can be worked on",
    onlySomeItems: "Only on some items",
    allowMultiple: "Allow more than one",
    moreOptionsFor: "More options for {name}",
    done: "Done",
    addOption: "+ Add an option",
    optionLabel: "Option {n}",
    optionsEmpty: "Add the answers people can pick from.",
    optionsEmptyOpen: "People write their own answer until you add some.",
    optionDuplicate: "That option is already on the list.",
    optionsLimit: "A list can hold up to {n} options.",
    groupPlaceholder: "For example, Inspection",
    renameGroup: "Rename group ({count})",
    renameGroupLabel: "New name for {name}",
    noSuggestedAnswer: "No suggested answer",
    suggestedAnswerNeedsOptions: "Add options first, then pick one to suggest.",
    whatToTellPeople: "What to tell people",
    whatToTellPeopleHint: "Shown where they fill it in, so they know what's needed.",
    whichItems: "Which items?",
    selectAll: "Select all",
    selectAllIn: "Select every item in {name}",
    itemsTicked: "{n} of {count} items",
    lastItem: "It has to stay on at least one item. To put it on every item, turn off Only on some items.",
    onlySomeItemsNoItems: "Add items to your checklists first.",
    groupSplit: "The {name} group is in two places. Keep a group in one place.",
    fieldOnNoItem: "{name} isn't on any item. Choose at least one, or turn off Only on some items.",
    fieldOnNoSection: "{name} is only on sections that are no longer on the dial. Remove it, or ask it of every section.",
    fieldOnNoSegment: "{name} is only on {nouns} that are no longer on the dial. Remove it, or ask it of every {noun}.",
    askOfEverySection: "Ask it of every section",
    askOfEverySegment: "Ask it of every {noun}",
    scaleOnNoItem: "Part of this dial came from its template and only covers items that are no longer on it. Put those items back, or tell us.",
    removeConsequences: {
      statusGates: "States that wait for it won't wait any more.",
      pairings: "Fields that had to be filled with it can be filled on their own.",
      resetClears: "Scheduled resets won't clear it any more.",
    },
    nudge: "Want people to record details?",
    addFields: "Add fields",
  },

  launch: {
    question: "Try it first, or go live.",
    checklistHeading: "Before it can go live",
    allClear: "Everything's ready.",
    fixThis: "Fix this",
    tryItFirst: {
      title: "Try it first",
      body: "A test copy appears on My Dials, just for you. Nothing you enter counts, and nothing is kept.",
    },
    testCopyBanner: "This is a test copy. Only you can see it.",
    backToEditing: "Back to editing",
    testCopyWarning: "Either way, this test copy and everything entered in it are deleted.",
    goLive: {
      title: "Go live",
      body: "Your dial appears on My Dials with you as its administrator. Then invite the people who'll use it.",
    },
    whereWillItRun: "Where will it run?",
    isLive: "Your dial is live",
    membersHeading: "Who's using this dial?",
    openDial: "Open your dial",
    goingLive: "Going live…",
    failed: "Your dial couldn't go live. Try again in a moment.",
    openTestCopy: "Open your test copy",
    makingTestCopy: "Making your test copy…",
    testCopyFailed: "Your test copy couldn't be made. Try again in a moment.",
    backToEditingFailed: "Couldn't go back to editing. Try again in a moment.",
  },

  designSettings: {
    title: "Design",
    onlyYou: "Only you see this, because you built this dial.",
    makeChanges: "Make changes",
    changesCount: "Changes · {n}",
    applyChanges: "Apply changes",
    added: "{n} new {nouns} will appear as Not started.",
    removed: "{n} removed {noun} and everything recorded against it will be deleted.",
    downloadFirst: "Download a copy first",
    newGroup: "The new {name} group will be empty until someone fills it in.",
    runElsewhere: "Run it somewhere else",
    addSite: "+ Add a site",
    archive: "Archive this design",
    archiveHint: "It stops counting against your plan. Running dials keep working, and nothing is deleted.",
    handOver: "Give this design to someone else",
    handOverHint: "People in your organisation who can build dials.",
    handOverConfirm:
      "Give {name} to {person}? You won't be able to change it any more. Everyone using the running dial keeps their access.",
    handOverButton: "Give it to {person}",
    handOverTestCopy: "Your test copy will be deleted.",
    leftBehindHeading: "Dials left by people who've gone",
    leftBehindWhy: { Left: "Left the organisation", Archived: "Account archived", Deleted: "Account deleted" },
    giveItTo: "Give it to…",
  },

  limits: {
    yourPlan: "Your plan",
    designsFallback: "{plan} has no room for another design.",
    close: "Close",
    talkToUs: "Talk to us",
    aiUsedUp: "You've used this month's {n} AI drafts. They reset on {date}. Templates and starting blank are always free.",
  },

  planMeter: {
    designs: "{used} of {allowed} designs on your plan",
    people: "{used} of {allowed} people on your plan",
    sites: "{used} of {allowed} sites for this design",
  },
} as const;

/** Fills a template's `{placeholders}`. A placeholder with no value is left as it is, so a missing value is visible, not blank. */
export function fill(template: string, values: Readonly<Record<string, string | number>>): string {
  return template.replace(/\{(\w+)\}/g, (match, key: string) => (key in values ? String(values[key]) : match));
}

/** The dial's own word for an action point, in every form the copy uses: {noun}, {nouns}, {Noun}, {Nouns} and {aNoun}. */
export function nounValues(noun: SegmentNoun): Record<string, string> {
  return { noun: noun.one, nouns: noun.many, Noun: noun.One, Nouns: noun.Many, aNoun: `${article(noun.one)} ${noun.one}` };
}

/** How many items an action point's checklist has: "No items yet", "1 item", "4 items". */
export function itemCount(n: number): string {
  if (n === 0) return builderCopy.build.itemCountNone;
  return n === 1 ? builderCopy.build.itemCountOne : fill(builderCopy.build.itemCountMany, { n });
}

/** How many fields a place on Details asks for: "No fields yet", "1 field", "4 fields". */
export function fieldCount(n: number): string {
  if (n === 0) return builderCopy.details.fieldCountNone;
  return n === 1 ? builderCopy.details.fieldCountOne : fill(builderCopy.details.fieldCountMany, { count: n });
}

/** A field as a sentence names it: its label, or "A field with no name" while it has none. */
export function fieldName(label: string): string {
  return label.trim() || builderCopy.details.unnamedField;
}
